import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, User, Mail, Phone, MapPin, Briefcase, Calendar, Loader2,
  FileText, Globe, Clock, Tag as TagIcon, Building2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

export default function AdminWaitlistDetail() {
  const navigate = useNavigate();
  const { entryId } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notesEdit, setNotesEdit] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuth();
  }, [entryId]);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (res.data.user_type !== 'admin') { navigate('/login'); return; }
      fetchEntry();
    } catch { navigate('/login'); }
  };

  const fetchEntry = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/waitlist/${entryId}/detail`, { withCredentials: true });
      setEntry(res.data);
      setNotesEdit(res.data.notes || '');
    } catch {
      toast.error('Waitlist entry not found');
      navigate('/admin/waitlist');
    } finally { setLoading(false); }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/waitlist/${entryId}/notes`, { notes: notesEdit }, { withCredentials: true });
      toast.success('Notes saved');
      setEntry(prev => ({ ...prev, notes: notesEdit }));
    } catch { toast.error('Failed to save notes'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!entry) return null;

  const STATUS_STYLE = {
    live: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    priority: 'bg-orange-100 text-orange-700 border-orange-200',
    early_interest: 'bg-sky-100 text-sky-700 border-sky-200',
    unmatched: 'bg-slate-100 text-slate-600 border-slate-200',
    converted: 'bg-purple-100 text-purple-700 border-purple-200',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="admin-waitlist-detail">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-slate-600 hover:text-slate-900" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Waitlist Entry</h1>
              <p className="text-xs text-slate-500">Admin read-only view</p>
            </div>
          </div>
          <button onClick={() => navigate('/admin/waitlist')} className="text-sm text-slate-500 hover:text-slate-700">
            Back to Waitlist
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Entry Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="waitlist-header-card">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shrink-0">
              {entry.user_type === 'tradesperson' ? <Briefcase className="w-7 h-7 text-white" /> : <Building2 className="w-7 h-7 text-white" />}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900" data-testid="entry-name">{entry.name || entry.full_name || 'Unknown'}</h2>
              <p className="text-slate-500 text-sm">{entry.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS_STYLE[entry.status] || STATUS_STYLE.unmatched}`}>
                  {(entry.status || 'unmatched').replace(/_/g, ' ')}
                </span>
                <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium capitalize">
                  {entry.user_type}
                </span>
                {entry.is_legacy && (
                  <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full text-xs border border-amber-200">Legacy</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Info */}
          <Section title="Personal Information" icon={<User className="w-4 h-4" />}>
            <InfoStack>
              <InfoRow icon={<User />} label="Full Name" value={entry.name || entry.full_name} />
              <InfoRow icon={<Mail />} label="Email" value={entry.email} />
              <InfoRow icon={<Phone />} label="Phone" value={entry.phone} />
              <InfoRow icon={<Briefcase />} label="User Type" value={entry.user_type} capitalize />
            </InfoStack>
          </Section>

          {/* Location */}
          <Section title="Location & Region" icon={<MapPin className="w-4 h-4" />}>
            <InfoStack>
              <InfoRow icon={<MapPin />} label="Postcode" value={entry.postcode} />
              <InfoRow icon={<Globe />} label="Town / City" value={entry.town_city} />
              <InfoRow icon={<MapPin />} label="County" value={entry.county} />
              <InfoRow icon={<TagIcon />} label="Matched Area" value={entry.area_name || (entry.matched_area_id ? entry.matched_area_id : null)} />
              <InfoRow icon={<TagIcon />} label="Area Status" value={entry.area_status} capitalize />
            </InfoStack>
          </Section>

          {/* Trade / Company Info */}
          <Section title={entry.user_type === 'tradesperson' ? 'Trade Information' : 'Company Information'} icon={<Briefcase className="w-4 h-4" />}>
            <InfoStack>
              {entry.user_type === 'tradesperson' ? (
                <>
                  <InfoRow icon={<Briefcase />} label="Trade" value={entry.trade || entry.trade_type} />
                  <InfoRow icon={<FileText />} label="Experience" value={entry.experience_years ? `${entry.experience_years} years` : null} />
                </>
              ) : (
                <>
                  <InfoRow icon={<Building2 />} label="Company" value={entry.company_name || entry.company} />
                  <InfoRow icon={<Briefcase />} label="Role" value={entry.role || entry.job_title} />
                </>
              )}
            </InfoStack>
          </Section>

          {/* Dates & Source */}
          <Section title="Submission Info" icon={<Calendar className="w-4 h-4" />}>
            <InfoStack>
              <InfoRow icon={<Calendar />} label="Submitted" value={entry.created_at ? new Date(entry.created_at).toLocaleString('en-GB') : null} />
              <InfoRow icon={<Clock />} label="Last Updated" value={entry.updated_at ? new Date(entry.updated_at).toLocaleString('en-GB') : null} />
              <InfoRow icon={<Globe />} label="Source" value={entry.source || entry.referral_source} capitalize />
              <InfoRow icon={<TagIcon />} label="Entry ID" value={entry.entry_id} mono />
            </InfoStack>
          </Section>
        </div>

        {/* Message / Notes */}
        {entry.message && (
          <Section title="Message" icon={<FileText className="w-4 h-4" />}>
            <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="entry-message">{entry.message}</p>
          </Section>
        )}

        {/* Admin Notes */}
        <Section title="Admin Notes" icon={<FileText className="w-4 h-4" />}>
          <textarea
            value={notesEdit}
            onChange={e => setNotesEdit(e.target.value)}
            rows={4}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-3"
            placeholder="Add admin notes about this waitlist entry..."
            data-testid="admin-notes-input"
          />
          <button
            onClick={handleSaveNotes}
            disabled={saving || notesEdit === (entry.notes || '')}
            className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-notes-btn"
          >
            {saving ? 'Saving...' : 'Save Notes'}
          </button>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-4">{icon}{title}</h2>
      {children}
    </div>
  );
}

function InfoStack({ children }) {
  return <div className="space-y-3">{children}</div>;
}

function InfoRow({ icon, label, value, capitalize, mono }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-slate-400 mt-0.5 shrink-0">{React.cloneElement(icon, { className: 'w-4 h-4' })}</span>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className={`text-sm font-medium text-slate-900 ${capitalize ? 'capitalize' : ''} ${mono ? 'font-mono text-xs' : ''} break-all`}>
          {value || <span className="text-slate-300">N/A</span>}
        </p>
      </div>
    </div>
  );
}
